import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Upload, Download } from '@mui/icons-material';
import { 
  parseCn61Csv, 
  convertCn61DataToCsv, 
  downloadCn61Csv, 
  generateCn61SampleCsv,
  applyCsvDataToCn61 
} from '../../../../utils/cn61CsvUtils';

interface RateData {
  rate: string | null;
}

interface FormatData {
  weight: RateData;
  items: RateData;
}

interface Cn61Rates {
  formatP: FormatData;
  formatG: FormatData;
  formatE: FormatData;
  formatX: FormatData;
  registeredItems: RateData;
  insuredItems: RateData;
  expressItems: RateData;
  mbagsWeight: RateData;
}

interface Cn61Data {
  ppo: string;
  year: number;
  direction: string;
  currency: string;
  inboundRates: Cn61Rates;
  outboundRates: Cn61Rates;
}

interface CN61TableProps {
  data: Cn61Data;
  onDataChange?: (updatedData: Cn61Data) => void;
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
  currentUserPpo?: string;
}

function CN61Table({ data, onDataChange, onError, onSuccess, currentUserPpo }: CN61TableProps): React.ReactElement {
  const [localData, setLocalData] = React.useState<Cn61Data>(data);
  const [uploadErrors, setUploadErrors] = React.useState<string[]>([]);
  const inboundFileInputRef = React.useRef<HTMLInputElement>(null);
  const outboundFileInputRef = React.useRef<HTMLInputElement>(null);

  // Stable input props
  const inputSx = useMemo(() => ({
    width: '100%',
    '& .MuiOutlinedInput-root': {
      height: '32px',
      fontSize: '0.875rem'
    }
  }), []);

  const inputPropsDecimal = useMemo(() => ({
    inputMode: 'decimal' as const,
    pattern: '^\\\d*(\\.\\d{0,3})?$',
    placeholder: '0.000'
  } as const), []);

  const formatDisplayValue = useCallback((val: string | number | null) => {
    if (val === null || val === undefined || val === '') return '';
    // Return as-is for string values (user is typing)
    return String(val);
  }, []);

  // Check if input has exactly 3 decimals (for visual validation)
  const hasValidFormat = useCallback((val: string | number | null) => {
    if (val === null || val === undefined || val === '') return false;
    const strVal = String(val);
    return /^\d+\.\d{3}$/.test(strVal);
  }, []);

  // Validate that all fields are filled with exactly 3 decimals
  const threeDecimalsRegex = useMemo(() => /^\d+\.\d{3}$/, []);
  const isValueComplete = useCallback((val: unknown) => {
    if (val === null || val === undefined || val === '') return false; // All fields must be filled
    const strVal = String(val);
    const numVal = parseFloat(strVal);
    if (isNaN(numVal)) return false;
    return threeDecimalsRegex.test(strVal);
  }, [threeDecimalsRegex]);

  const isDirectionComplete = useCallback((rates: Cn61Rates | null) => {
    if (!rates) {
      console.log('[CN61Table] No rates to validate');
      return false; // No data means can't download
    }
    
    let allComplete = true;
    
    Object.entries(rates).forEach(([key, entry]: [string, any]) => {
      if (entry && typeof entry === 'object') {
        // Check if it has weight/items structure
        if ('weight' in entry && entry.weight) {
          const complete = isValueComplete(entry.weight.rate);
          console.log(`[CN61Table] ${key}.weight.rate = "${entry.weight.rate}" -> complete: ${complete}`);
          if (!complete) allComplete = false;
        }
        if ('items' in entry && entry.items) {
          const complete = isValueComplete(entry.items.rate);
          console.log(`[CN61Table] ${key}.items.rate = "${entry.items.rate}" -> complete: ${complete}`);
          if (!complete) allComplete = false;
        }
        // Check if it has direct rate field
        if ('rate' in entry) {
          const complete = isValueComplete(entry.rate);
          console.log(`[CN61Table] ${key}.rate = "${entry.rate}" -> complete: ${complete}`);
          if (!complete) allComplete = false;
        }
      }
    });
    
    console.log(`[CN61Table] All fields complete: ${allComplete}`);
    
    // All fields must be filled with exactly 3 decimals
    return allComplete;
  }, [isValueComplete]);

  // Update local data when prop data changes
  React.useEffect(() => {
    if (data) {
      setLocalData(data);
    }
  }, [data]);

  if (!data) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }} data-testid="cn61-table-empty">
        <Typography sx={{ color: '#666', fontStyle: 'italic' }} data-testid="cn61-table-empty-text">
          No data available
        </Typography>
      </Box>
    );
  }

  const { ppo, year, direction, currency, inboundRates, outboundRates } = localData;
  const hasInboundData = inboundRates !== null;
  const hasOutboundData = outboundRates !== null;

  if (!hasInboundData && !hasOutboundData) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }} data-testid="cn61-table-no-input">
        <Typography sx={{ color: '#666', fontStyle: 'italic' }} data-testid="cn61-table-no-input-text">
          No data available
        </Typography>
      </Box>
    );
  }

  // Get dynamic rows from BE data instead of hardcoding
  const getDynamicRows = (rates: Cn61Rates) => {
    const rows: Array<{
      key: string;
      label: string;
      hasWeight: boolean;
      hasItems: boolean;
      data: any;
    }> = [];

    // Iterate through the actual data from BE
    Object.entries(rates).forEach(([key, value]) => {
      if (value && typeof value === 'object') {
        const hasWeight = 'weight' in value;
        const hasItems = 'items' in value;
        const hasRate = 'rate' in value;
        
        // Add rows if they have weight/items structure OR just rate structure
        if (hasWeight || hasItems || hasRate) {
          rows.push({
            key,
            label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
            hasWeight,
            hasItems,
            data: value,
          });
        }
      }
    });

    return rows;
  };

  const handleInputChange = React.useCallback((
    direction: 'inbound' | 'outbound',
    rowKey: string, 
    field: 'weight' | 'items' | 'rate', 
    value: string
  ) => {
    setLocalData((prev) => {
      const newData = { ...prev };
      const ratesKey = direction === 'inbound' ? 'inboundRates' : 'outboundRates';
      
      if (newData[ratesKey]) {
        const updatedRates = { ...newData[ratesKey] };
        
        // Initialize the rowKey object if it doesn't exist
        if (!updatedRates[rowKey as keyof typeof updatedRates]) {
          (updatedRates as any)[rowKey] = {};
        }
        
        if (field === 'rate') {
          // Handle direct rate field (for items like registeredItems, insuredItems, etc.)
          (updatedRates as any)[rowKey].rate = value === '' ? null : value;
        } else {
          // Handle weight/items structure (for formatP, formatG, etc.)
          if (!(updatedRates as any)[rowKey][field]) {
            (updatedRates as any)[rowKey][field] = {};
          }
          (updatedRates as any)[rowKey][field].rate = value === '' ? null : value;
        }
        
        newData[ratesKey] = updatedRates as any;
      }
      
      // Notify parent component of changes
      if (onDataChange) {
        onDataChange(newData);
      }
      
      return newData;
    });
  }, [onDataChange]);

  const handleInputBlur = React.useCallback((
    direction: 'inbound' | 'outbound',
    rowKey: string, 
    field: 'weight' | 'items' | 'rate', 
    value: string
  ) => {
    const sanitized = value.trim();
    const isValid = /^\d*(\.\d{0,3})?$/.test(sanitized);
    if (isValid && sanitized !== '') {
      const numericValue = parseFloat(sanitized);
      const formattedValue = isNaN(numericValue) ? '' : numericValue.toFixed(3);
      
      setLocalData((prev) => {
        const newData = { ...prev };
        const ratesKey = direction === 'inbound' ? 'inboundRates' : 'outboundRates';
        
        if (newData[ratesKey]) {
          const updatedRates = { ...newData[ratesKey] };
          
          // Initialize the rowKey object if it doesn't exist
          if (!updatedRates[rowKey as keyof typeof updatedRates]) {
            (updatedRates as any)[rowKey] = {};
          }
          
          if (field === 'rate') {
            // Handle direct rate field (for items like registeredItems, insuredItems, etc.)
            (updatedRates as any)[rowKey].rate = formattedValue === '' ? null : formattedValue;
          } else {
            // Handle weight/items structure (for formatP, formatG, etc.)
            if (!(updatedRates as any)[rowKey][field]) {
              (updatedRates as any)[rowKey][field] = {};
            }
            (updatedRates as any)[rowKey][field].rate = formattedValue === '' ? null : formattedValue;
          }
          
          newData[ratesKey] = updatedRates as any;
        }
        
        // Notify parent component of changes
        if (onDataChange) {
          onDataChange(newData);
        }
        
        return newData;
      });
    }
  }, [onDataChange]);

  // Handle CSV file upload for specific direction
  const handleCsvUpload = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>, direction: 'inbound' | 'outbound') => {
    const file = event.target.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log(`File selected for ${direction}:`, file.name, file.size, 'bytes');
    
    // Clear previous errors
    setUploadErrors([]);

    try {
      const text = await file.text();
      console.log('File content length:', text.length);
      console.log('File content preview:', text.substring(0, 200));
      
      // Parse CSV with year validation
      const csvData = parseCn61Csv(text, localData, currentUserPpo, localData.year);
      console.log('Parsed CSV data:', csvData);
      
      // Filter CSV data to only include the specified direction
      const filteredCsvData = csvData.filter(row => row.direction === direction);
      console.log(`Filtered CSV data for ${direction}:`, filteredCsvData);
      
      if (filteredCsvData.length === 0) {
        console.log(`No valid data rows found for ${direction}`);
        const errorMsg = `CSV file contains no valid data rows for ${direction} direction`;
        setUploadErrors([errorMsg]);
        onError?.(errorMsg);
        return;
      }

      console.log(`Applying CSV data to ${direction} data...`);
      // Apply CSV data to the current data structure
      const updatedData = applyCsvDataToCn61(filteredCsvData, localData);
      console.log('Updated data:', updatedData);
      
      setLocalData(updatedData);
      
      // Notify parent component of changes
      if (onDataChange) {
        console.log('Notifying parent component of changes');
        onDataChange(updatedData);
      }
      
      // Clear any previous errors on successful upload
      setUploadErrors([]);
      onSuccess?.(`Successfully imported ${filteredCsvData.length} rate entries for ${direction} from CSV`);
      
      // Reset file input
      const fileInputRef = direction === 'inbound' ? inboundFileInputRef : outboundFileInputRef;
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error during CSV upload:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Split multiple errors if they were joined with |||
      const errorList = errorMessage.includes('|||') 
        ? errorMessage.split('|||') 
        : [`Error parsing CSV file: ${errorMessage}`];
      
      setUploadErrors(errorList);
      onError?.(errorList.join('\n'));
    }
  }, [localData, onDataChange, onError, onSuccess, currentUserPpo]);

  // Handle CSV download for specific direction
  const handleCsvDownload = React.useCallback((direction: 'inbound' | 'outbound') => {
    try {
      // Guard: block download if any present fields are incomplete
      const targetRates = direction === 'inbound' ? localData.inboundRates : localData.outboundRates;
      console.log(`[CN61Table] Checking ${direction} rates completeness:`, targetRates);
      
      // Check if there's any data at all
      if (!targetRates) {
        console.log(`[CN61Table] Blocking download - no ${direction} data structure`);
        onError?.(`Cannot download: No ${direction} data available`);
        return;
      }
      
      const isComplete = isDirectionComplete(targetRates);
      console.log(`[CN61Table] ${direction} rates complete:`, isComplete);
      
      if (!isComplete) {
        console.log(`[CN61Table] Blocking download - incomplete or empty rates detected`);
        onError?.('Please fill all TD rates with exactly 3 decimals before downloading');
        return;
      }

      // Create a filtered data structure for the specific direction
      const filteredData = {
        ...localData,
        inboundRates: direction === 'inbound' ? localData.inboundRates : null,
        outboundRates: direction === 'outbound' ? localData.outboundRates : null
      };
      
      const csvContent = convertCn61DataToCsv(filteredData, currentUserPpo);
      const filename = direction === 'inbound' 
        ? `td_rates_${currentUserPpo}_${localData.ppo}_${localData.year}.csv`
        : `td_rates_${localData.ppo}_${currentUserPpo}_${localData.year}.csv`;
      downloadCn61Csv(csvContent, filename);
      onSuccess?.(`CSV file for ${direction} downloaded successfully`);
    } catch (error) {
      console.error(`[CN61Table] Download error:`, error);
      onError?.(`Error generating CSV file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [localData, currentUserPpo, onSuccess, onError, isDirectionComplete]);

  // Handle sample CSV download
  const handleSampleCsvDownload = React.useCallback(() => {
    try {
      const sampleCsv = generateCn61SampleCsv(localData.ppo, localData.year.toString());
      const filename = `cn61-upu-sample-${localData.ppo}-${localData.year}.csv`;
      downloadCn61Csv(sampleCsv, filename);
      onSuccess?.('Sample CSV file downloaded successfully');
    } catch (error) {
      onError?.(`Error generating sample CSV file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [localData.ppo, localData.year, onSuccess, onError]);

  // Background colors based on direction
  const getBackgroundColor = (isInbound: boolean, isMainRow: boolean) => {
    if (isInbound) {
      return isMainRow ? '#e8f5e8' : '#f0f8f0'; // Green shades for inbound
    } else {
      return isMainRow ? '#ffe8e8' : '#fff0f0'; // Red shades for outbound
    }
  };

  // Helper function to render a single table for a specific direction
  const renderDirectionTable = (rates: Cn61Rates, isInbound: boolean, direction: string) => {
    const directionKey = isInbound ? 'inbound' : 'outbound';
    const dynamicRows = getDynamicRows(rates);
    const fileInputRef = isInbound ? inboundFileInputRef : outboundFileInputRef;

    return (
      <TableContainer component={Paper} sx={{ mb: 3 }} data-testid={`cn61-table-${direction.toLowerCase()}`}>
        <Table size="small" sx={{ border: '1px solid #e0e0e0' }}>
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #e0e0e0',
                  textAlign: 'center'
                }}
                colSpan={2}
              >
                {direction}
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: '#f5f5f5', 
                  width: '125px',
                  border: '1px solid #e0e0e0',
                  textAlign: 'center'
                }}
              >
                Rate
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dynamicRows.map((rowDef) => (
              <React.Fragment key={rowDef.key}>
                {/* Weight row */}
                {rowDef.hasWeight && (
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600, 
                        backgroundColor: getBackgroundColor(isInbound, true),
                        border: '1px solid #e0e0e0',
                        borderBottom: rowDef.hasItems ? '1px solid #e0e0e0' : '1px solid #e0e0e0'
                      }}
                      rowSpan={rowDef.hasItems ? 2 : 1}
                    >
                      {rowDef.label}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        backgroundColor: getBackgroundColor(isInbound, false),
                        border: '1px solid #e0e0e0',
                        borderBottom: rowDef.hasItems ? '1px solid #e0e0e0' : '1px solid #e0e0e0',
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        fontWeight: 500,
                        width: '120px'
                      }}
                    >
                      Weight
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        border: '1px solid #e0e0e0',
                        borderBottom: rowDef.hasItems ? '1px solid #e0e0e0' : '1px solid #e0e0e0'
                      }}
                    >
                      <TextField
                        size="small"
                        type="text"
                        value={formatDisplayValue(rowDef.data.weight?.rate)}
                        onChange={(e) => handleInputChange(directionKey, rowDef.key, 'weight', e.target.value)}
                        onBlur={(e) => handleInputBlur(directionKey, rowDef.key, 'weight', e.target.value)}
                        sx={{
                          ...inputSx,
                          '& .MuiOutlinedInput-root': {
                            ...inputSx['& .MuiOutlinedInput-root'],
                            '& fieldset': {
                              borderColor: rowDef.data.weight?.rate && !hasValidFormat(rowDef.data.weight.rate) ? '#d32f2f' : undefined,
                            },
                          },
                        }}
                        inputProps={inputPropsDecimal}
                        error={rowDef.data.weight?.rate ? !hasValidFormat(rowDef.data.weight.rate) : false}
                      />
                    </TableCell>
                  </TableRow>
                )}

                {/* Items row */}
                {rowDef.hasItems && (
                  <TableRow>
                    {!rowDef.hasWeight && (
                      <TableCell 
                        sx={{ 
                          fontWeight: 600, 
                          backgroundColor: getBackgroundColor(isInbound, true),
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        {rowDef.label}
                      </TableCell>
                    )}
                    <TableCell 
                      sx={{ 
                        backgroundColor: getBackgroundColor(isInbound, false),
                        border: '1px solid #e0e0e0',
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        fontWeight: 500,
                        width: '120px'
                      }}
                    >
                      Items
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                      <TextField
                        size="small"
                        type="text"
                        value={formatDisplayValue(rowDef.data.items?.rate)}
                        onChange={(e) => handleInputChange(directionKey, rowDef.key, 'items', e.target.value)}
                        onBlur={(e) => handleInputBlur(directionKey, rowDef.key, 'items', e.target.value)}
                        sx={{
                          ...inputSx,
                          '& .MuiOutlinedInput-root': {
                            ...inputSx['& .MuiOutlinedInput-root'],
                            '& fieldset': {
                              borderColor: rowDef.data.items?.rate && !hasValidFormat(rowDef.data.items.rate) ? '#d32f2f' : undefined,
                            },
                          },
                        }}
                        inputProps={inputPropsDecimal}
                        error={rowDef.data.items?.rate ? !hasValidFormat(rowDef.data.items.rate) : false}
                      />
                    </TableCell>
                  </TableRow>
                )}

                {/* Single rate row (for items like registeredItems, insuredItems, etc.) */}
                {!rowDef.hasWeight && !rowDef.hasItems && 'rate' in rowDef.data && (
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600, 
                        backgroundColor: getBackgroundColor(isInbound, true),
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      {rowDef.label}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        backgroundColor: getBackgroundColor(isInbound, false),
                        border: '1px solid #e0e0e0',
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        fontWeight: 500,
                        width: '120px'
                      }}
                    >
                      {rowDef.key === 'mbagsWeight' ? 'Weight' : 'Items'}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                      <TextField
                        size="small"
                        type="text"
                        value={formatDisplayValue(rowDef.data.rate)}
                        onChange={(e) => handleInputChange(directionKey, rowDef.key, 'rate', e.target.value)}
                        onBlur={(e) => handleInputBlur(directionKey, rowDef.key, 'rate', e.target.value)}
                        sx={{
                          ...inputSx,
                          '& .MuiOutlinedInput-root': {
                            ...inputSx['& .MuiOutlinedInput-root'],
                            '& fieldset': {
                              borderColor: rowDef.data.rate && !hasValidFormat(rowDef.data.rate) ? '#d32f2f' : undefined,
                            },
                          },
                        }}
                        inputProps={inputPropsDecimal}
                        error={rowDef.data.rate ? !hasValidFormat(rowDef.data.rate) : false}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        
        {/* Hidden file input for CSV upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={(e) => handleCsvUpload(e, directionKey)}
          style={{ display: 'none' }}
          data-testid={`cn61-csv-file-input-${direction.toLowerCase()}`}
        />
      </TableContainer>
    );
  };

  return (
    <Box sx={{ width: '100%' }} data-testid="cn61-table-container">

      {/* Upload Errors Display */}
      {uploadErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }} data-testid="cn61-upload-errors">
          <AlertTitle>Upload Errors</AlertTitle>
          <List dense sx={{ pt: 0 }}>
            {uploadErrors.map((error, index) => (
              <ListItem key={index} sx={{ py: 0 }}>
                <ListItemText primary={error} />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      {/* CN61 Data Tables */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: hasInboundData && hasOutboundData ? 'row' : 'column', 
        gap: 4,
        flexWrap: 'wrap'
      }}>
        {/* Inbound Table */}
        {hasInboundData && (
          <Box 
            data-testid="cn61-inbound-section"
            sx={{ 
              flex: hasInboundData && hasOutboundData ? '1 1 45%' : '1',
              minWidth: '500px',
              maxWidth: '500px'
            }}
          >
            {renderDirectionTable(inboundRates, true, 'Inbound')}
            
            {/* Action buttons below inbound table */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2, mb: 3 }}>
              <Button
                variant="outlined"
                onClick={() => handleCsvDownload('inbound')}
                sx={{
                  fontSize: '12px',
                  textTransform: 'none',
                  borderColor: '#00ACE7',
                  color: '#00ACE7',
                  '&:hover': {
                    borderColor: '#0095C7',
                    backgroundColor: '#E3F7FE'
                  }
                }}
                data-testid="cn61-download-inbound-csv-btn"
              >
                Download TD Rates
              </Button>
              <Button
                variant="outlined"
                onClick={() => inboundFileInputRef.current?.click()}
                sx={{
                  fontSize: '12px',
                  textTransform: 'none',
                  borderColor: '#00ACE7',
                  color: '#00ACE7',
                  '&:hover': {
                    borderColor: '#0095C7',
                    backgroundColor: '#E3F7FE'
                  }
                }}
                data-testid="cn61-upload-inbound-csv-btn"
              >
                Upload TD Rates
              </Button>
            </Box>
          </Box>
        )}

        {/* Outbound Table */}
        {hasOutboundData && (
          <Box 
            data-testid="cn61-outbound-section"
            sx={{ 
              flex: hasInboundData && hasOutboundData ? '1 1 45%' : '1',
              minWidth: '500px',
              maxWidth: '500px'
            }}
          >
            {renderDirectionTable(outboundRates, false, 'Outbound')}
            
            {/* Action buttons below outbound table */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2, mb: 3 }}>
              <Button
                variant="outlined"
                onClick={() => handleCsvDownload('outbound')}
                sx={{
                  fontSize: '12px',
                  textTransform: 'none',
                  borderColor: '#00ACE7',
                  color: '#00ACE7',
                  '&:hover': {
                    borderColor: '#0095C7',
                    backgroundColor: '#E3F7FE'
                  }
                }}
                data-testid="cn61-download-outbound-csv-btn"
              >
                Download TD Rates
              </Button>
              <Button
                variant="outlined"
                onClick={() => outboundFileInputRef.current?.click()}
                sx={{
                  fontSize: '12px',
                  textTransform: 'none',
                  borderColor: '#00ACE7',
                  color: '#00ACE7',
                  '&:hover': {
                    borderColor: '#0095C7',
                    backgroundColor: '#E3F7FE'
                  }
                }}
                data-testid="cn61-upload-outbound-csv-btn"
              >
                Upload TD Rates
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default CN61Table;
